import { Stack, Switch, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconBell } from '@tabler/icons-react'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import UserDetails from '@/components/Details/UserDetails/UserDetails'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import OptionsMenu from '@/components/OptionsMenu'
import { SpecialInputText } from '@/components/SpecialInputText'
import { LAMINA1_OFFICIAL_ADDRESS } from '@/constants/blockchain'
import { formatValueToSuffix } from '@/utils/utils'

import MembersList from './MembersList'
import classes from './style.module.css'

interface SpaceInfoStatsProps {
  title: string
  value: number | string
  onClick?: () => void
}

const SpaceInfoStats: React.FC<SpaceInfoStatsProps> = ({ title, value, onClick }) => (
  <Stack
    gap="5px"
    style={{ alignItems: 'center', cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <Title order={4}>{value}</Title>
    <Text>{title}</Text>
  </Stack>
)
interface SpaceInfoProps {
  nMembers: number
  members: string[]
  nPosts: number
  spaceRequiresEmail: boolean
  hasJoined: boolean
  userEmail: string
  joinSpace: (email: string) => void
  leaveSpace: () => void
}

const SpacesInfo: React.FC<SpaceInfoProps> = ({
  nMembers,
  members,
  nPosts,
  spaceRequiresEmail,
  hasJoined,
  userEmail,
  joinSpace,
  leaveSpace,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const description =
    'Welcome to the Official Space Page for LAMINA1. Create, manage, and share digital content effortlessly. On this platform, you own your assets, identity, and experiences across diverse platforms and storyworlds. Seamless creation and distribution of next-gen digital content. Join to stay up to date with the latest on LAMINA1.'

  const [opened, { open, close }] = useDisclosure(false)
  const [openedLeave, { open: openLeave, close: closeLeave }] = useDisclosure(false)
  const [openedEmail, { open: openEmail, close: closeEmail }] = useDisclosure(false)

  // Email settings
  const [emailInput, setEmailInput] = useState<string>('')
  const [deleteToggle, setDeleteToggle] = useState<boolean>(false)

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value)
  }, [])

  const handleDeleteToggle = useCallback(() => {
    setEmailInput('')
    setDeleteToggle(prev => !prev)
  }, [])

  const isEmailValid = useMemo(() => {
    const re = /\S+@\S+\.\S+/
    return re.test(emailInput)
  }, [emailInput])

  const handleLeave = useCallback(() => {
    leaveSpace()
    closeLeave()
  }, [leaveSpace, closeLeave])

  const handleCloseEmail = useCallback(() => {
    // Reset always
    setEmailInput('')
    setDeleteToggle(false)
    closeEmail()
  }, [closeEmail])

  const handleModifyEmail = useCallback(() => {
    // Modify email
    if (!deleteToggle) {
      joinSpace(emailInput)
    } else if (spaceRequiresEmail) {
      // If deleting, leave space if it requires the user to have an email
      leaveSpace()
    } else {
      // If not, just remove the email
      joinSpace('')
    }
    handleCloseEmail()
  }, [joinSpace, spaceRequiresEmail, emailInput, leaveSpace, handleCloseEmail])

  return (
    <Stack className={classes.sideBar}>
      <Stack gap="30px">
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Title order={3}>{t('spaces.spaceInfo.title', 'Description')}</Title>
          {hasJoined && (
            <OptionsMenu>
              <a onClick={openLeave}>{t('spaces.spaceInfo.leaveSpace', 'Leave Space')}</a>
              <a onClick={openEmail}>
                {userEmail !== ''
                  ? t('spaces.spaceInfo.modifyEmail', 'Modify Email')
                  : t('spaces.spaceInfo.addEmail', 'Add Email')}
              </a>
            </OptionsMenu>
          )}
        </Stack>
        <Text>{description}</Text>
      </Stack>
      <Stack gap="30px" style={{ flexDirection: 'row' }}>
        <SpaceInfoStats
          title="Members"
          value={formatValueToSuffix(nMembers, 3, 0) || 0}
          onClick={open}
        />
        <SpaceInfoStats title="Posts" value={formatValueToSuffix(nPosts, 3, 0) || 0} />
      </Stack>
      <Stack gap="30px">
        <Title order={3}>Creator</Title>
        <UserDetails userId={LAMINA1_OFFICIAL_ADDRESS} />
      </Stack>
      <Stack gap="30px">
        <Title order={3}>Owner</Title>
        <UserDetails userId={LAMINA1_OFFICIAL_ADDRESS} />
      </Stack>
      <ModalSkeleton title="Members" opened={opened} onClose={close}>
        <MembersList members={members} />
      </ModalSkeleton>
      <ModalSkeleton title="Leave Space" opened={openedLeave} onClose={closeLeave}>
        <Stack>
          <Text>
            <Trans i18nKey="spaces.spaceInfo.leaveSpaceText">
              Are you sure you want to leave this space? You will lose access to all the exclusive
              content.
            </Trans>
          </Text>
          <Stack style={{ flexDirection: 'row' }}>
            <RootButton expand style1={false} secondary onClick={closeLeave}>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton expand style1={false} onClick={handleLeave}>
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
        </Stack>
      </ModalSkeleton>
      <ModalSkeleton
        title={userEmail !== '' ? 'Modify Email' : 'Add Email'}
        opened={openedEmail}
        onClose={handleCloseEmail}
      >
        <Stack>
          {userEmail !== '' ? (
            <>
              <Text>
                The current email you have provided to this Space is{' '}
                <Title order={4}>{userEmail}</Title>
              </Text>
              <Text>
                <Trans i18nKey="spaces.spaceInfo.emailText">
                  You can enter a different email below. Use the toggle to delete the email instead
                  of modifying it.
                </Trans>
              </Text>
              {userEmail !== '' && (
                <Switch checked={deleteToggle} onChange={handleDeleteToggle} label="Delete" />
              )}
              {!deleteToggle && (
                <SpecialInputText
                  iconLeft={<IconBell size={20} color={theme.colors.l1Primary[0]} />}
                  placeholder="Enter your email"
                  value={emailInput}
                  onChange={handleEmailChange}
                />
              )}
              {deleteToggle && (
                <>
                  <Text>
                    <Trans i18nKey="spaces.spaceInfo.deleteEmailText">
                      Are you sure you want to delete your email from this Space? You might stop
                      receiving updates about this Space.
                    </Trans>
                  </Text>
                  {spaceRequiresEmail && (
                    <Text>
                      <Trans i18nKey="spaces.spaceInfo.deleteEmailText2">
                        This Space requires an email. If you delete your email, you will be removed
                        from the Space.
                      </Trans>
                    </Text>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Text>
                <Trans i18nKey="spaces.spaceInfo.addEmailText">
                  Add an email to receive updates about this Space. You can update or remove your
                  email at any time.
                </Trans>
              </Text>
              <SpecialInputText
                iconLeft={<IconBell size={20} color={theme.colors.l1Primary[0]} />}
                placeholder="Enter your email"
                value={emailInput}
                onChange={handleEmailChange}
              />
            </>
          )}
          <Stack style={{ flexDirection: 'row' }}>
            <RootButton expand style1={false} secondary onClick={handleCloseEmail}>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton
              expand
              style1={false}
              onClick={handleModifyEmail}
              disabled={!deleteToggle && !isEmailValid}
            >
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
        </Stack>
      </ModalSkeleton>
    </Stack>
  )
}

export default SpacesInfo
